import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type TextStyle, type ViewStyle } from "react-native";

import {
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../lib/notifications";
import type { NotificationKind, NotificationRow } from "../../types/database";
import { Skeleton } from "../ui";
import Svg, { Path } from "react-native-svg";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED_LIGHT = "rgba(28,43,26,0.62)";
const TEXT_LIGHT_LIGHT = "rgba(28,43,26,0.45)";

const W = "rgba(255,255,255,1)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER_DARK = "rgba(255,255,255,0.07)";

export type FilterDef = {
  /** Stable key used by `useState`. */
  key: string;
  /** Visible chip label. */
  label: string;
  /** Notification kinds that match this filter. Empty/undefined = match all (used by "All"); special value `"unread"` = unread filter. */
  match: "all" | "unread" | NotificationKind[];
};

/**
 * Default chip set — mirrors the original /notifications page (student-flavored).
 * Other portals override via the `filters` prop.
 */
export const STUDENT_FILTERS: FilterDef[] = [
  { key: "all", label: "All", match: "all" },
  { key: "unread", label: "Unread", match: "unread" },
  {
    key: "deliverables",
    label: "Deliverables",
    match: ["deliverable_due_soon", "deliverable_graded", "gate_passed", "gate_failed", "placement_announced"],
  },
  {
    key: "team",
    label: "Team",
    match: ["team_invite_received", "team_member_joined", "team_member_left", "auto_merge_warning"],
  },
  {
    key: "announcements",
    label: "Announcements",
    match: ["announcement"],
  },
  // Note: student-to-student DMs and connection requests were removed from
  // the product, so the old "social" chip is gone. Announcements remain.
];

/**
 * Client-flavored filter set. Same data, different lens — recruiters care about
 * incoming submissions, candidate flow, judging, and shortlist updates rather
 * than team chatter.
 */
export const CLIENT_FILTERS: FilterDef[] = [
  { key: "all", label: "All", match: "all" },
  { key: "unread", label: "Unread", match: "unread" },
  {
    key: "submissions",
    label: "Submissions",
    match: ["deliverable_graded", "gate_passed", "gate_failed", "placement_announced"],
  },
  {
    key: "candidates",
    label: "Candidates",
    match: ["team_invite_received", "team_member_joined", "team_member_left", "auto_merge_warning"],
  },
  {
    key: "messages",
    label: "Messages",
    match: ["dm_received", "announcement", "connection_request", "connection_accepted"],
  },
];

function kindLabel(kind: NotificationKind): string {
  switch (kind) {
    case "team_invite_received": return "Team invite";
    case "team_member_joined": return "Team update";
    case "team_member_left": return "Team update";
    case "deliverable_due_soon": return "Due soon";
    case "deliverable_graded": return "Graded";
    case "gate_passed": return "Gate passed";
    case "gate_failed": return "Gate failed";
    case "placement_announced": return "Placement";
    case "auto_merge_warning": return "Auto-merge";
    case "dm_received": return "Message";
    case "connection_request": return "Connection";
    case "connection_accepted": return "Connection";
    case "prize_pool_awarded": return "Prize";
    case "judge_invited": return "Judging";
    case "announcement": return "Announcement";
    default: return "Activity";
  }
}

function formatRelativeTime(iso: string): string {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return "";
  const diffMs = Date.now() - created;
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Compact time stamp for the activity-row right column.
 *  ≤59 min → "12m"; <24h → "3h"; yesterday → "Yesterday"; <7d → "2d";
 *  older → "Apr 12". Premium look = no "ago" suffix. */
function formatRelativeTimeCompact(iso: string): string {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return "";
  const now = Date.now();
  const diffMs = now - created;
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Per-kind icon glyph + tint for the activity row's leading circle.
 *  Tint colors are deliberately desaturated — the activity feed is
 *  scan-first, so glyphs read as a quiet category cue, not a neon. */
type IconTone = "amber" | "sage" | "rose" | "neutral";

function kindIconConfig(kind: NotificationKind): {
  glyph: "chat" | "clock" | "users" | "check" | "megaphone" | "trophy" | "alert" | "user-add" | "gavel" | "spark";
  tone: IconTone;
} {
  switch (kind) {
    case "dm_received":
      return { glyph: "chat", tone: "amber" };
    case "deliverable_due_soon":
      return { glyph: "clock", tone: "rose" };
    case "deliverable_graded":
      return { glyph: "check", tone: "sage" };
    case "gate_passed":
      return { glyph: "check", tone: "sage" };
    case "gate_failed":
      return { glyph: "alert", tone: "rose" };
    case "placement_announced":
      return { glyph: "trophy", tone: "amber" };
    case "team_invite_received":
      return { glyph: "user-add", tone: "amber" };
    case "team_member_joined":
      return { glyph: "users", tone: "sage" };
    case "team_member_left":
      return { glyph: "users", tone: "neutral" };
    case "auto_merge_warning":
      return { glyph: "alert", tone: "rose" };
    case "connection_request":
      return { glyph: "user-add", tone: "amber" };
    case "connection_accepted":
      return { glyph: "check", tone: "sage" };
    case "prize_pool_awarded":
      return { glyph: "trophy", tone: "amber" };
    case "judge_invited":
      return { glyph: "gavel", tone: "amber" };
    case "announcement":
      return { glyph: "megaphone", tone: "amber" };
    default:
      return { glyph: "spark", tone: "neutral" };
  }
}

function toneColors(tone: IconTone, isDark: boolean): { bg: string; fg: string } {
  if (tone === "amber") {
    return {
      bg: isDark ? "rgba(181,135,58,0.16)" : "rgba(181,135,58,0.14)",
      fg: COLORS.AMBER_DEEP.hex,
    };
  }
  if (tone === "sage") {
    return {
      bg: isDark ? "rgba(143,187,138,0.16)" : "rgba(100,127,77,0.14)",
      fg: "#5A7440",
    };
  }
  if (tone === "rose") {
    return {
      bg: isDark ? "rgba(192,57,43,0.18)" : "rgba(192,57,43,0.10)",
      fg: "#A23A2D",
    };
  }
  return {
    bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(28,43,26,0.06)",
    fg: isDark ? "rgba(255,255,255,0.65)" : "rgba(28,43,26,0.55)",
  };
}

function KindIcon({ kind, isDark }: { kind: NotificationKind; isDark: boolean }) {
  const { glyph, tone } = kindIconConfig(kind);
  const { bg, fg } = toneColors(tone, isDark);
  return (
    <View
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        backgroundColor: bg,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <KindGlyph glyph={glyph} color={fg} />
    </View>
  );
}

function KindGlyph({
  glyph,
  color,
}: {
  glyph: ReturnType<typeof kindIconConfig>["glyph"];
  color: string;
}) {
  const w = 16;
  switch (glyph) {
    case "chat":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M2.5 4.5C2.5 3.4 3.4 2.5 4.5 2.5h7C12.6 2.5 13.5 3.4 13.5 4.5v5C13.5 10.6 12.6 11.5 11.5 11.5H6L3.5 13.5V11.5C3 11.5 2.5 11 2.5 10.5v-6Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      );
    case "clock":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M8 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"
            stroke={color}
            strokeWidth={1.5}
            fill="none"
          />
          <Path d="M8 5v3.2L10 9.6" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </Svg>
      );
    case "users":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M5.6 7.2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm5 0a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4Z"
            stroke={color}
            strokeWidth={1.4}
            fill="none"
          />
          <Path
            d="M2 13c0-2 1.6-3.5 3.6-3.5S9.2 11 9.2 13M10 9.5c1.7 0 3 1.2 3 3"
            stroke={color}
            strokeWidth={1.4}
            strokeLinecap="round"
            fill="none"
          />
        </Svg>
      );
    case "check":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M8 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"
            stroke={color}
            strokeWidth={1.5}
            fill="none"
          />
          <Path d="M5.5 8.4 7.2 10l3.3-3.4" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </Svg>
      );
    case "megaphone":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M3 6.2v3.6h2.5L12 12V4L5.5 6.2H3Z"
            stroke={color}
            strokeWidth={1.4}
            strokeLinejoin="round"
            fill="none"
          />
          <Path d="M5.5 9.8V12c0 .6.4 1 1 1H7c.6 0 1-.4 1-1v-1" stroke={color} strokeWidth={1.4} strokeLinecap="round" fill="none" />
        </Svg>
      );
    case "trophy":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M5 3.5h6V7c0 1.7-1.3 3-3 3s-3-1.3-3-3V3.5Z"
            stroke={color}
            strokeWidth={1.4}
            strokeLinejoin="round"
            fill="none"
          />
          <Path
            d="M5 5H3.5C3.5 6.5 4.2 7.5 5.4 7.8M11 5h1.5c0 1.5-.7 2.5-1.9 2.8M6 13h4M8 10v3"
            stroke={color}
            strokeWidth={1.4}
            strokeLinecap="round"
            fill="none"
          />
        </Svg>
      );
    case "alert":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M8 2.5 1.8 12.5h12.4L8 2.5Z"
            stroke={color}
            strokeWidth={1.4}
            strokeLinejoin="round"
            fill="none"
          />
          <Path d="M8 6.5v3" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
          <Path d="M8 11.2v.6" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
        </Svg>
      );
    case "user-add":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M6.4 7.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z"
            stroke={color}
            strokeWidth={1.4}
            fill="none"
          />
          <Path
            d="M2.5 13c0-2.2 1.8-4 3.9-4s3.9 1.8 3.9 4"
            stroke={color}
            strokeWidth={1.4}
            strokeLinecap="round"
            fill="none"
          />
          <Path d="M12 6v3M10.5 7.5h3" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
        </Svg>
      );
    case "gavel":
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M3 9 9 3M5.5 11.5 11.5 5.5M2 14h6"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path d="M7 6.5 11 2.5l2.5 2.5L9.5 9 7 6.5Z" stroke={color} strokeWidth={1.4} strokeLinejoin="round" fill="none" />
        </Svg>
      );
    case "spark":
    default:
      return (
        <Svg width={w} height={w} viewBox="0 0 16 16" fill="none">
          <Path
            d="M8 2.5v3M8 10.5v3M2.5 8h3M10.5 8h3M4.5 4.5l2 2M9.5 9.5l2 2M11.5 4.5l-2 2M6.5 9.5l-2 2"
            stroke={color}
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </Svg>
      );
  }
}

function matchesFilter(filter: FilterDef, n: NotificationRow): boolean {
  if (filter.match === "all") return true;
  if (filter.match === "unread") return !n.read_at;
  return filter.match.includes(n.kind);
}

type Theme = "light" | "dark";

export type ActivityInboxBodyProps = {
  /** Light theme = sand bg, white cards, forest text. Dark theme = black bg, off-black cards, white text. */
  theme: Theme;
  /** Filter chips. Defaults to STUDENT_FILTERS. */
  filters?: FilterDef[];
  /**
   * Hero band content. Caller controls the title + emphasis to match the
   * portal voice. e.g. student = "Your activity"; client = "Channel activity".
   * Required unless `hideHero` is true.
   */
  heroTitle?: string | null;
  heroEmphasis?: string | null;
  heroSubtitle?: string | null;
  /** Override "INBOX" overline if needed. */
  heroOverline?: string | null;
  /**
   * Skip the hero band entirely. Use when embedding in a context that
   * already supplies its own title (e.g. the student header overlay).
   */
  hideHero?: boolean;
};

/**
 * The full inbox UI minus the shell. Mount inside a `StudentPortalShell` /
 * `ClientPortalShell` / etc.
 */
export function ActivityInboxBody({
  theme,
  filters = STUDENT_FILTERS,
  heroTitle,
  heroEmphasis,
  heroSubtitle,
  heroOverline = "Inbox",
  hideHero = false,
}: ActivityInboxBodyProps) {
  const isDark = theme === "dark";

  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterKey, setFilterKey] = useState<string>(filters[0]?.key ?? "all");
  // When a notification is opened, we expand it inline inside the inbox
  // (overlay) instead of routing away.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const list = await listMyNotifications(80);
      setRows(list);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const activeFilter = useMemo(
    () => filters.find((f) => f.key === filterKey) ?? filters[0],
    [filterKey, filters]
  );
  const filtered = useMemo(
    () => rows.filter((n) => matchesFilter(activeFilter, n)),
    [rows, activeFilter]
  );
  const unreadCount = useMemo(() => rows.filter((n) => !n.read_at).length, [rows]);

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const f of filters) {
      m[f.key] = rows.filter((n) => matchesFilter(f, n)).length;
    }
    return m;
  }, [filters, rows]);

  async function onMarkAllRead() {
    await markAllNotificationsRead();
    const now = new Date().toISOString();
    setRows((prev) => prev.map((r) => ({ ...r, read_at: r.read_at ?? now })));
  }

  async function onPressNotification(n: NotificationRow) {
    // Expand the row inline inside the inbox/overlay. We deliberately do
    // NOT route to n.link_path — the user wants the message to open in
    // place rather than send them to a separate page.
    setExpandedId((prev) => (prev === n.id ? null : n.id));
    if (!n.read_at) {
      await markNotificationRead(n.id);
      setRows((prev) =>
        prev.map((r) => (r.id === n.id ? { ...r, read_at: new Date().toISOString() } : r))
      );
    }
  }

  // Theme tokens.
  const bgPage = isDark ? COLORS.BLACK.hex : COLORS.SAND.hex;
  const bgHero = isDark ? COLORS.BLACK.hex : COLORS.LINEN.hex;
  const heroBorder = isDark ? "rgba(255,255,255,0.07)" : SAND_DARK;
  const titleColor = isDark ? W : FOREST;
  const subtitleColor = isDark ? WM : TEXT_MUTED_LIGHT;
  const cardBg = isDark ? COLORS.OFF_BLACK.hex : COLORS.WHITE.hex;
  const cardBorder = isDark ? BORDER_DARK : SAND_DARK;
  const rowBorder = isDark ? "rgba(255,255,255,0.06)" : "rgba(28,43,26,0.06)";
  const textBody = isDark ? W : FOREST;
  const textMuted = isDark ? WM : TEXT_MUTED_LIGHT;
  const textFaint = isDark ? WL : TEXT_LIGHT_LIGHT;

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: bgPage }}
      contentContainerStyle={{ paddingBottom: 64 }}
    >
      {/* Hero band — skipped when embedded in a context that supplies its
          own title (e.g. the student header overlay). */}
      {!hideHero ? (
        <View
          style={{
            paddingHorizontal: 20,
            paddingVertical: 28,
            backgroundColor: bgHero,
            borderBottomWidth: 1,
            borderBottomColor: heroBorder,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              {heroOverline ?? "Inbox"}
            </Text>
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: "CormorantGaramond_400Regular",
              fontSize: 32,
              lineHeight: 36,
              color: titleColor,
            }}
          >
            {heroTitle ?? ""}{" "}
            <Text
              style={{
                fontFamily: "CormorantGaramond_400Regular",
                fontStyle: "italic",
                color: AMBER,
              }}
            >
              {heroEmphasis ?? ""}
            </Text>
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: "DMSans_300Light",
              fontSize: 14,
              lineHeight: 22,
              color: subtitleColor,
              maxWidth: 640,
            }}
          >
            {heroSubtitle ?? ""}
          </Text>
        </View>
      ) : null}

      {/* Filter chips + body */}
      <View
        style={{
          paddingHorizontal: 20,
          paddingTop: 20,
          maxWidth: 920,
          width: "100%" as unknown as number,
          alignSelf: "center",
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap", position: "relative", zIndex: 80 }}>
          <FilterDropdown
            filters={filters}
            counts={counts}
            value={filterKey}
            onChange={setFilterKey}
            isDark={isDark}
          />
          {unreadCount > 0 ? (
            <Pressable
              onPress={() => void onMarkAllRead()}
              style={({ hovered }) => [
                {
                  marginLeft: "auto" as unknown as number,
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.45)",
                  backgroundColor: "transparent",
                },
                Platform.OS === "web" && hovered
                  ? { backgroundColor: "rgba(181,135,58,0.08)", borderColor: AMBER }
                  : null,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 10,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: AMBER,
                }}
              >
                Mark all read
              </Text>
            </Pressable>
          ) : null}
        </View>

        <View style={{ marginTop: 20 }}>
          {loading ? (
            <View
              style={{
                overflow: "hidden",
                borderRadius: 6,
                borderWidth: 1,
                borderColor: cardBorder,
                backgroundColor: cardBg,
              }}
            >
              {[0, 1, 2, 3, 4].map((k) => (
                <View
                  key={k}
                  style={{
                    flexDirection: "row",
                    gap: 12,
                    paddingHorizontal: 20,
                    paddingVertical: 16,
                    borderTopWidth: k === 0 ? 0 : 1,
                    borderTopColor: rowBorder,
                  }}
                >
                  <Skeleton width={3} height={48} radius={2} />
                  <View style={{ flex: 1 }}>
                    <Skeleton width={80} height={9} />
                    <Skeleton width={"70%" as unknown as number} height={16} style={{ marginTop: 10 }} />
                    <Skeleton width={"55%" as unknown as number} height={11} style={{ marginTop: 6 }} />
                  </View>
                </View>
              ))}
            </View>
          ) : filtered.length === 0 ? (
            <View
              style={{
                paddingHorizontal: 24,
                paddingVertical: 40,
                borderRadius: 6,
                borderWidth: 1,
                borderColor: cardBorder,
                backgroundColor: cardBg,
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: "CormorantGaramond_500Medium",
                  fontSize: 18,
                  color: textBody,
                }}
              >
                {filterKey === "unread" ? "Nothing unread" : "Nothing here yet"}
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: "DMSans_400Regular",
                  fontSize: 12.5,
                  color: textMuted,
                  textAlign: "center",
                  maxWidth: 420,
                }}
              >
                {filterKey === "unread"
                  ? "All caught up. New activity will appear here."
                  : "We'll let you know when there's something to see."}
              </Text>
            </View>
          ) : (
            <View
              style={{
                overflow: "hidden",
                borderRadius: 6,
                borderWidth: 1,
                borderColor: cardBorder,
                backgroundColor: cardBg,
              }}
            >
              {filtered.map((n, idx) => (
                <View key={n.id}>
                  <ActivityRow
                    row={n}
                    first={idx === 0}
                    onPress={() => void onPressNotification(n)}
                    isDark={isDark}
                    textBody={textBody}
                    textMuted={textMuted}
                    textFaint={textFaint}
                    rowBorder={rowBorder}
                  />
                  {expandedId === n.id ? (
                    <ExpandedDetail
                      row={n}
                      isDark={isDark}
                      onClose={() => setExpandedId(null)}
                    />
                  ) : null}
                </View>
              ))}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

/**
 * Brand-aligned dropdown that replaces the old chip row.
 * Sand surface, forest text, amber accents on the active option.
 */
function FilterDropdown({
  filters,
  counts,
  value,
  onChange,
  isDark,
}: {
  filters: FilterDef[];
  counts: Record<string, number>;
  value: string;
  onChange: (k: string) => void;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const active = filters.find((f) => f.key === value) ?? filters[0];
  const isWeb = Platform.OS === "web";

  // Light tokens (sand/linen). Dark tokens (forest/off-black) for the
  // dark-themed inbox embedding.
  const surface = isDark ? COLORS.OFF_BLACK.hex : COLORS.WHITE.hex;
  const surfaceHover = isDark ? "#1f1f1f" : "rgba(247,244,238,0.8)";
  const border = isDark ? "rgba(255,255,255,0.10)" : SAND_DARK;
  const borderHover = isDark ? "rgba(181,135,58,0.45)" : "rgba(181,135,58,0.4)";
  const text = isDark ? W : FOREST;
  const muted = isDark ? WM : TEXT_MUTED_LIGHT;
  const menuShadow = "0 22px 44px rgba(28,43,26,0.16)";

  return (
    <View style={{ position: "relative", minWidth: 220, zIndex: 80 }}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel="Filter activity"
        style={({ hovered }) => [
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 14,
            backgroundColor: surface,
            borderWidth: 1,
            borderColor: border,
            borderRadius: 4,
            paddingVertical: 10,
            paddingHorizontal: 14,
          },
          isWeb
            ? ({
                cursor: "pointer",
                transition: "border-color 0.18s ease, background-color 0.18s ease",
              } as const)
            : null,
          isWeb && (hovered || open)
            ? { borderColor: borderHover, backgroundColor: surfaceHover }
            : null,
        ]}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: 8.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: AMBER,
              marginBottom: 2,
            }}
          >
            Filter
          </Text>
          <Text
            style={{
              fontFamily: "DMSans_600SemiBold",
              fontSize: 13,
              color: text,
            }}
            numberOfLines={1}
          >
            {active?.label ?? "All"}
          </Text>
        </View>
        <View
          style={[
            {
              transform: [{ rotate: open ? "180deg" : "0deg" }],
            },
            isWeb ? ({ transition: "transform 0.18s ease" } as object) : null,
          ]}
        >
          <Svg width={10} height={10} viewBox="0 0 10 10" fill="none">
            <Path
              d="M2 3.5l3 3 3-3"
              stroke={muted}
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      </Pressable>

      {open ? (
        <View
          style={[
            {
              position: "absolute",
              top: 56,
              left: 0,
              right: 0,
              backgroundColor: surface,
              borderWidth: 1,
              borderColor: border,
              borderRadius: 4,
              paddingVertical: 4,
              zIndex: 9999,
              elevation: 12,
            },
            isWeb ? ({ boxShadow: menuShadow } as object) : null,
          ]}
        >
          {filters.map((f) => {
            const selected = value === f.key;
            const ct = counts[f.key] ?? 0;
            return (
              <Pressable
                key={f.key}
                onPress={() => {
                  onChange(f.key);
                  setOpen(false);
                }}
                style={({ hovered }) => [
                  {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    paddingVertical: 9,
                    paddingHorizontal: 14,
                    backgroundColor: selected ? "rgba(181,135,58,0.08)" : "transparent",
                  },
                  isWeb && hovered && !selected
                    ? { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(28,43,26,0.04)" }
                    : null,
                  isWeb ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                  <View
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      borderWidth: 1.2,
                      borderColor: selected ? AMBER : (isDark ? "rgba(255,255,255,0.32)" : "rgba(28,43,26,0.32)"),
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {selected ? (
                      <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: AMBER }} />
                    ) : null}
                  </View>
                  <Text
                    style={{
                      fontFamily: selected ? "DMSans_600SemiBold" : "DMSans_500Medium",
                      fontSize: 12.5,
                      color: selected ? AMBER : text,
                    }}
                    numberOfLines={1}
                  >
                    {f.label}
                  </Text>
                </View>
                <Text
                  style={{
                    fontFamily: "DMSans_500Medium",
                    fontSize: 11,
                    color: muted,
                    letterSpacing: 0.4,
                  }}
                >
                  {ct}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}


/**
 * Inline expanded notification view rendered below a row when the user
 * clicks it. Shows the title + full body in place of routing the user
 * away to a detail page. Stays inside the activity overlay.
 */
function ExpandedDetail({
  row,
  isDark,
  onClose,
}: {
  row: NotificationRow;
  isDark: boolean;
  onClose: () => void;
}) {
  const titleColor = isDark ? W : FOREST;
  const bodyColor = isDark ? WM : TEXT_MUTED_LIGHT;
  const surface = isDark ? "rgba(255,255,255,0.04)" : "rgba(247,244,238,0.7)";
  const dividerColor = isDark ? "rgba(255,255,255,0.07)" : SAND_DARK;
  return (
    <View
      style={{
        backgroundColor: surface,
        borderTopWidth: 1,
        borderTopColor: dividerColor,
        paddingVertical: 16,
        paddingHorizontal: 18,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 14,
          marginBottom: 6,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 9,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          {kindLabel(row.kind)}
        </Text>
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Collapse"
          style={[
            {
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(28,43,26,0.14)",
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: "DMSans_600SemiBold",
              fontSize: 9.5,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: bodyColor,
            }}
          >
            Collapse
          </Text>
        </Pressable>
      </View>
      <Text
        style={{
          fontFamily: "CormorantGaramond_500Medium",
          fontSize: 22,
          lineHeight: 28,
          color: titleColor,
          marginBottom: 8,
        }}
      >
        {row.title}
      </Text>
      {row.body ? (
        <Text
          // `whiteSpace` is a CSS-on-web extension RN's TextStyle doesn't
          // model. Cast through unknown so we keep the runtime behavior
          // (preserve hard line breaks in notification bodies) without
          // the prior `as unknown as undefined` hack that masked the
          // mismatch by lying about the value's type.
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 13.5,
            lineHeight: 22,
            color: bodyColor,
            whiteSpace: "pre-wrap",
          } as unknown as TextStyle}
        >
          {row.body}
        </Text>
      ) : (
        <Text
          style={{
            fontFamily: "DMSans_400Regular",
            fontSize: 12,
            lineHeight: 18,
            color: bodyColor,
            fontStyle: "italic",
          }}
        >
          No additional details.
        </Text>
      )}
      <Text
        style={{
          marginTop: 12,
          fontFamily: "DMSans_500Medium",
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: isDark ? WL : TEXT_LIGHT_LIGHT,
        }}
      >
        {formatRelativeTime(row.created_at)}
      </Text>
    </View>
  );
}

function FilterChip({
  label,
  active,
  count,
  onPress,
  isDark,
}: {
  label: string;
  active: boolean;
  count: number;
  onPress: () => void;
  isDark: boolean;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";

  const inactiveBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.6)";
  const inactiveBorder = isDark ? "rgba(255,255,255,0.10)" : SAND_DARK;
  const inactiveColor = isDark ? WM : TEXT_MUTED_LIGHT;
  const inactiveColorHover = isDark ? W : FOREST;
  const activeBg = "rgba(181,135,58,0.10)";
  const activeBorder = "rgba(181,135,58,0.45)";

  const tone = active ? AMBER : web && hover ? inactiveColorHover : inactiveColor;
  return (
    <Pressable
      onPress={onPress}
      {...(web ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>) : {})}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? activeBorder : inactiveBorder,
          backgroundColor: active ? activeBg : inactiveBg,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        } as ViewStyle,
        web ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: tone,
        }}
      >
        {label}
      </Text>
      {count > 0 ? (
        <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 10.5, color: tone, opacity: 0.75 }}>
          {count}
        </Text>
      ) : null}
    </Pressable>
  );
}

function ActivityRow({
  row,
  first,
  onPress,
  isDark,
  textBody,
  textMuted,
  textFaint,
  rowBorder,
}: {
  row: NotificationRow;
  first: boolean;
  onPress: () => void;
  isDark: boolean;
  textBody: string;
  textMuted: string;
  textFaint: string;
  rowBorder: string;
}) {
  const [hover, setHover] = useState(false);
  const isUnread = !row.read_at;
  const web = Platform.OS === "web";

  // Hover wash is the same regardless of read state — the unread cue is
  // carried by the amber dot in the right column rather than a row tint, so
  // unread rows don't paint a faint amber background that fights with the
  // icon tints on the left.
  const hoverBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(28,43,26,0.03)";

  return (
    <Pressable
      onPress={onPress}
      {...(web
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as Record<string, () => void>)
        : {})}
      style={[
        {
          paddingHorizontal: 18,
          paddingVertical: 14,
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 14,
          borderTopWidth: first ? 0 : 1,
          borderTopColor: rowBorder,
          backgroundColor: web && hover ? hoverBg : "transparent",
        } as ViewStyle,
        web ? ({ cursor: "pointer", transition: "background-color 0.15s ease" } as object) : null,
      ]}
    >
      {/* Leading circular icon — quiet category cue. */}
      <View style={{ paddingTop: 1 }}>
        <KindIcon kind={row.kind} isDark={isDark} />
      </View>

      {/* Body — title + optional supporting line. Title is sans bold, sized
          to read at a glance; the supporting line stays muted and clamps to
          two lines so long announcements don't push the row tall. */}
      <View style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        <Text
          numberOfLines={1}
          style={{
            fontFamily: isUnread ? "DMSans_700Bold" : "DMSans_600SemiBold",
            fontSize: 14,
            lineHeight: 20,
            color: textBody,
          }}
        >
          {row.title}
        </Text>
        {row.body ? (
          <Text
            numberOfLines={2}
            style={{
              marginTop: 3,
              fontFamily: "DMSans_400Regular",
              fontSize: 13,
              lineHeight: 19,
              color: textMuted,
            }}
          >
            {row.body}
          </Text>
        ) : null}
      </View>

      {/* Trailing cluster — compact relative time on top, amber unread dot
          beneath it (only when the row hasn't been read). */}
      <View style={{ alignItems: "flex-end", paddingTop: 3, gap: 6 }}>
        <Text
          style={{
            fontFamily: "DMSans_500Medium",
            fontSize: 11.5,
            color: textFaint,
            fontVariant: ["tabular-nums"],
          }}
        >
          {formatRelativeTimeCompact(row.created_at)}
        </Text>
        {isUnread ? (
          <View
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              backgroundColor: AMBER,
            }}
          />
        ) : null}
      </View>
    </Pressable>
  );
}
